import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, styled } from '@mui/material';
import { TFunction } from 'i18next';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { useSnackbar } from '@cnx-saas/snackbar';
import { RhfTextField } from '@cnx-saas/common-fe';
import { usePasswordResetUpdateMutation } from '../../../api/tenant/mutations/reset-password';

type ResetPasswordInput = {
  email: string;
  password: string;
  password2: string;
};

type ResetPasswordFormProps = {
  email: string;
};

export const ResetPasswordForm = ({ email }: ResetPasswordFormProps) => {
  const { t } = useTranslation();
  const { addAlert } = useSnackbar();
  const navigate = useNavigate();

  const { control, handleSubmit } = useForm<ResetPasswordInput>({
    resolver: yupResolver(registrationFormSchema(t)),
    defaultValues: {
      email: email,
    },
  });

  const [resetPassword] = usePasswordResetUpdateMutation();

  const onSubmit = async (input: ResetPasswordInput) => {
    const response = await resetPassword({
      variables: {
        details: {
          password: input.password,
          email: input.email,
        },
      },
    });

    if (response.errors || response.data?.resetPassword.message !== 'ok') {
      addAlert({
        type: 'error',
        children: response.data?.resetPassword.message,
      });
    } else {
      addAlert({
        type: 'success',
        children: t('userManagement.resetPassword.success', {
          defaultValue: 'Password has been changed',
        }),
      });
      navigate('/login');
    }
  };
  return (
    <Root onSubmit={handleSubmit(onSubmit)}>
      <Box display={'flex'} flexDirection={'column'}>
        <RhfTextField
          control={control}
          name="email"
          disabled={true}
          label={t('userManagement.filter.email', {
            defaultValue: 'email',
          })}
        />{' '}
        <RhfTextField
          control={control}
          name="password"
          type="password"
          label={t('registration.formLabel.userPassword', {
            defaultValue: 'password',
          })}
        />
        <RhfTextField
          control={control}
          type="password"
          name="password2"
          label={t('registration.formLabel.userPasswordConfirmation', {
            defaultValue: 'Re-enter Password',
          })}
        />
        <Button type="submit">
          {t('userManagement.resetPassword.update', {
            defaultValue: 'Update',
          })}
        </Button>
      </Box>
    </Root>
  );
};

export const Root = styled('form')({
  width: '100%',
});

export const registrationFormSchema = (t: TFunction) => {
  const fieldRequiredMessage = t(
    'registration.form.validation-error.required',
    {
      defaultValue: 'This field is required.',
    },
  );
  const passwordValidationMessage = {
    minLength: t('registration.form.validation-error.minLength', {
      defaultValue: 'Password must be at least 8 characters long.',
    }),
    hasUpperCase: t('registration.form.validation-error.hasUpperCase', {
      defaultValue: 'Password must contain at least one uppercase letter.',
    }),
    hasLowerCase: t('registration.form.validation-error.hasLowerCase', {
      defaultValue: 'Password must contain at least one lowercase letter.',
    }),
    hasDigit: t('registration.form.validation-error.hasDigit', {
      defaultValue: 'Password must contain at least one digit.',
    }),
    hasSpecialChar: t('registration.form.validation-error.hasSpecialChar', {
      defaultValue: 'Password must contain at least one special character.',
    }),
  };

  return yup.object().shape({
    email: yup.string().required(fieldRequiredMessage),
    password: yup
      .string()
      .required(fieldRequiredMessage)
      .test('minLength', passwordValidationMessage.minLength, (value) =>
        value ? value.length >= 8 : false,
      )
      .test('hasUpperCase', passwordValidationMessage.hasUpperCase, (value) =>
        value ? /[A-Z]/.test(value) : false,
      )
      .test('hasLowerCase', passwordValidationMessage.hasLowerCase, (value) =>
        value ? /[a-z]/.test(value) : false,
      )
      .test('hasDigit', passwordValidationMessage.hasDigit, (value) =>
        value ? /\d/.test(value) : false,
      )
      .test(
        'hasSpecialChar',
        passwordValidationMessage.hasSpecialChar,
        (value) => (value ? /[!@#$%^&*(),.?":{}|<>]/.test(value) : false),
      ),
    password2: yup
      .string()
      .oneOf(
        [yup.ref('password')],
        t('registration.form.validation-error.passwordMatch', {
          defaultValue: 'Passwords must match.',
        }),
      )
      .required(fieldRequiredMessage),
  });
};
